import React from 'react';

export interface RailInteractionProps {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: (e: React.FocusEvent) => void;
  onBlur: (e: React.FocusEvent) => void;
}

/**
 * Hover / focus expansion state for the navigation rail.
 *
 * Lives in AppLayout rather than inside Sidebar because the expanded width is
 * now a LAYOUT dimension — the workspace and the AppBar are pushed by it, so
 * the same boolean has to reach three components. Sidebar just spreads
 * `railProps` onto the drawer paper.
 *
 * The focus rule is the subtle part. Expanding on plain `focus` looks right
 * until you click a nav item: the click focuses the button, the pointer leaves,
 * `hovered` goes false — and the rail stays open forever because the clicked
 * item still holds focus. So only KEYBOARD focus counts. `:focus-visible` is
 * exactly the browser's own record of "did this focus come from the keyboard",
 * and it is false for a mouse click on a button or link, which is the case we
 * need to exclude. Tab-to-expand still works; click-and-leave now collapses.
 */
export const useRailState = (pinned: boolean) => {
  const [hovered, setHovered] = React.useState(false);
  const [keyboardFocus, setKeyboardFocus] = React.useState(false);

  const railProps = React.useMemo<RailInteractionProps>(
    () => ({
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
      onFocus: (e: React.FocusEvent) => {
        let viaKeyboard = true;
        try {
          viaKeyboard = (e.target as HTMLElement).matches(':focus-visible');
        } catch {
          // Pre-2022 Safari has no :focus-visible selector and throws on the
          // match. Falling back to "treat as keyboard" keeps the rail reachable
          // by Tab there; the worst case is the old sticky behaviour, never an
          // unreachable menu.
          viaKeyboard = true;
        }
        if (viaKeyboard) setKeyboardFocus(true);
      },
      onBlur: (e: React.FocusEvent) => {
        // focusout bubbles, so this fires for every item. Only a focus move
        // that leaves the rail entirely should collapse it.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setKeyboardFocus(false);
        }
      },
    }),
    []
  );

  return { expanded: pinned || hovered || keyboardFocus, railProps };
};
